/**
 * Outbox-Katalog (Ruling R29): welche Ziele es gibt, welcher Worker sie
 * abholt und welche Typen mit welcher Payload eingereiht werden duerfen.
 *
 * Genau EIN Worker je Ziel. Die Worker leiten ihre WHERE-Klausel aus
 * `OUTBOX_ZUSTAENDIGKEIT` ab; eine zweite Stelle, die dieselben Zeilen zieht,
 * gibt es nicht (sonst Doppel-Export / Doppel-Mail).
 *
 *  integrationen (apps/api/src/integrationen/outbox.ts): woo, mail, carrier, druck
 *  belege        (apps/api/src/belege/worker.ts):        belege, buchhaltung (Ausgangsbelege und Eingangsrechnungen)
 */
import { OUTBOX_ZIELE } from './entitaeten.js';

export type OutboxZiel = (typeof OUTBOX_ZIELE)[number];
export type OutboxWorker = 'integrationen' | 'belege';

export const OUTBOX_ZUSTAENDIGKEIT: Record<OutboxZiel, OutboxWorker> = {
  woo: 'integrationen',
  mail: 'integrationen',
  carrier: 'integrationen',
  belege: 'belege',
  buchhaltung: 'belege',
  druck: 'integrationen',
};

/** Ziele eines Workers, fuer `ziel IN (...)`. */
export function outboxZieleFuer(worker: OutboxWorker): OutboxZiel[] {
  return OUTBOX_ZIELE.filter((z) => OUTBOX_ZUSTAENDIGKEIT[z] === worker);
}

/**
 * Verbindliche Typen, Keys und Payloads (nur IDs, R5). Wer einreiht, haelt sich daran;
 * wer abholt, liest genau diese Felder.
 *
 * | ziel        | typ                     | idempotenz_key                                   | payload |
 * |-------------|-------------------------|--------------------------------------------------|---------|
 * | woo         | woo.order_status        | woo.order_status:<order_id>:completed            | { externalId, status } |
 * | woo         | woo.order_note          | woo.order_note:<zweck>:<id>                      | { externalId, note, kundenNotiz:false } |
 * | woo         | woo.stock               | woo.stock:<listing_id> (ein Slot je Listing)     | { listingId, externalProductId, externalVariationId, menge } |
 * | woo         | woo.stock_produktion    | woo.stock_produktion:<position_id>:<listing_id>[:storno] (R127, relativ, Marke im Shop) | { listingId, externalProductId, externalVariationId, delta, art, buchungId, positionId, sku } |
 * | mail        | mail.versand            | mail.versand:<shipment_id>                       | { shipmentId, orderId } |
 * | mail        | mail.rechnung           | mail.beleg:<document_id>                         | { document_id, order_id, art, nummer } |
 * | mail        | mail.gutschrift         | mail.beleg:<document_id>                         | { document_id, order_id, art, nummer } |
 * | mail        | mail.rueckfrage_adresse | frei                                             | { orderId, problem } |
 * | mail        | mail.retourenlabel      | mail.retourenlabel:<return_id>                   | { returnId, orderId } (Ausnahme der Leitung, R37) |
 * | druck       | druck.label             | druck.label:<parcel_id>[:nachdruck:<n>]          | { parcelId, kennung } |
 * | druck       | druck.lieferschein      | druck.lieferschein:<document_id>                 | { documentId, kennung } |
 * | belege      | belege.rechnung         | belege.rechnung:<order_id>                       | { order_id } |
 * | belege      | belege.gutschrift       | belege.gutschrift:kanal:<order_id>:<refund_id>   | { order_id, betrag_cents, grund, quelle, referenz?, retoure_id?, positionen? } |
 * | belege      | belege.lieferschein     | belege.lieferschein:<shipment_id>                | { shipment_id, order_id, geschenk? } |
 * | buchhaltung | buchhaltung.export      | buchhaltung.export:<document_id>                 | { document_id, order_id, art, nummer } |
 * | buchhaltung | buchhaltung.eingangsrechnung | buchhaltung.eingangsrechnung:<eingangsrechnung_id> | { eingangsrechnung_id } (beim Festschreiben, R97) |
 */
export const OUTBOX_TYPEN = {
  woo: ['woo.order_status', 'woo.order_note', 'woo.stock', 'woo.stock_produktion'],
  mail: ['mail.versand', 'mail.rechnung', 'mail.gutschrift', 'mail.rueckfrage_adresse', 'mail.retourenlabel'],
  carrier: [],
  belege: ['belege.rechnung', 'belege.gutschrift', 'belege.lieferschein'],
  buchhaltung: ['buchhaltung.export', 'buchhaltung.eingangsrechnung'],
  druck: ['druck.label', 'druck.lieferschein'],
} as const satisfies Record<OutboxZiel, readonly string[]>;

export type OutboxTyp = (typeof OUTBOX_TYPEN)[OutboxZiel][number];

/** Ziel eines Typs (null = unbekannter Typ). */
export function outboxZielVon(typ: string): OutboxZiel | null {
  for (const z of OUTBOX_ZIELE) if ((OUTBOX_TYPEN[z] as readonly string[]).includes(typ)) return z;
  return null;
}
